/**
 * Raster effects, the C64 demo staple: horizontal colour bars bobbing on sines, and colour-cycling
 * strips. Everything is whole pixels in palette colours.
 */

import type { Screen } from '../../render/screen';
import { wrapIndex } from './menuModel';
import { calmStep } from './theme';

export interface RasterBar {
  readonly ramp: readonly number[];
  /** Radians per millisecond. */
  readonly speed: number;
  readonly phase: number;
}

/** Where a bar's centre is at `ms`, and how near it is (for drawing order). Pure. */
export function barPosition(
  bar: RasterBar, top: number, height: number, lineH: number, ms: number,
): { y: number; z: number } {
  const size = bar.ramp.length * lineH;
  const a = ms * bar.speed + bar.phase;
  const travel = Math.max(0, (height - size) / 2);
  return { y: Math.round(top + height / 2 - size / 2 + Math.sin(a) * travel), z: Math.cos(a) };
}

/** Bars bobbing through the band [top, top + height), each colour `lineH` lines tall. */
export function drawRasterBars(
  screen: Screen, top: number, height: number, ms: number, bars: readonly RasterBar[],
  lineH = 1, x = 0, w = screen.width,
): void {
  const placed = bars.map((bar) => ({ bar, ...barPosition(bar, top, height, lineH, ms) }));
  placed.sort((a, b) => a.z - b.z);
  for (const { bar, y } of placed) {
    bar.ramp.forEach((colour, i) => screen.fill(x, y + i * lineH, w, lineH, colour));
  }
}

/**
 * A strip of `rows` one-pixel lines from x0 to x1 whose colours flow along it (`dir` 1 = rightwards),
 * each row a step behind the one above, so the stripes lean.
 */
export function drawRasterStrip(
  screen: Screen, x0: number, x1: number, y: number, rows: number, ms: number, ramp: readonly number[],
  dir = 1, seg = 4,
): void {
  if (x1 <= x0) return;
  const shift = Math.floor(ms / calmStep(70));
  for (let r = 0; r < rows; r++) {
    for (let x = x0; x < x1; x += seg) {
      const k = dir > 0 ? Math.floor((x - x0) / seg) : Math.floor((x1 - x) / seg);
      const colour = ramp[wrapIndex(k - shift + r, ramp.length)] ?? 1;
      screen.fill(x, y + r, Math.min(seg, x1 - x), 1, colour);
    }
  }
}

/** A one-line colour cycle across the whole width: a separator with life in it. */
export function drawRasterRule(
  screen: Screen, y: number, ms: number, ramp: readonly number[], x0 = 0, x1 = screen.width,
): void {
  drawRasterStrip(screen, x0, x1, y, 1, ms, ramp, 1, 8);
}
